import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';
import {
  DateTimeInput,
  Form,
  NumberInput,
  SelectInput,
  TextInput,
  minValue,
  required,
  useDataProvider,
  useNotify,
  useRedirect,
} from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { RequireAccess } from '../auth/RequireAccess';
import { type AccessibleRegion } from '../auth/permissions';
import type { AdminDataProvider, Market } from '../dataProvider';

/**
 * Promo code create. Gated promo_codes:issue (super_admin): creating a code
 * commits real value, the value-committing direction of the trust split
 * (D-promo-codes). A viewer or editor is denied directly, not just missing the
 * button.
 *
 * MARKET IS EXPLICIT and one-per-code (D-promo-codes): a GLOBAL admin chooses
 * uk/us deliberately; a single-region admin has market PRE-SET and LOCKED to their
 * own market and cannot create outside their scope (enforced in the submit
 * handler, not just the form). New codes default active=true, usedCount=0,
 * effectType=fixed_wallet_credit (the only member today).
 *
 * A create produces an action='create' audit entry automatically through the
 * wrapper; there is no audit code here.
 */

const regionToMarket = (region: string): Market =>
  region.toLowerCase() as Market;

interface PromoCreateValues {
  code: string;
  market: string;
  effectValue: number;
  validFrom: string;
  validTo: string;
  maxRedemptions: number | string | null;
}

const toIso = (v: string): string => new Date(v).toISOString();

const PromoCodeCreateForm = () => {
  const { accessibleRegions } = useRegion();
  const dataProvider = useDataProvider<AdminDataProvider>();
  const notify = useNotify();
  const redirect = useRedirect();

  const locked = accessibleRegions.length === 1;
  const lockedMarket = locked ? regionToMarket(accessibleRegions[0]) : '';

  const marketChoices = accessibleRegions.map((r) => ({
    id: regionToMarket(r),
    name: r,
  }));

  const onSubmit = async (raw: Record<string, unknown>) => {
    const values = raw as unknown as PromoCreateValues;
    const market = locked ? lockedMarket : values.market;

    // Region guard — cannot create a code outside the admin's scope, even if the
    // form were bypassed.
    if (!accessibleRegions.includes(market.toUpperCase() as AccessibleRegion)) {
      notify('You cannot create a promo code outside your region', {
        type: 'error',
      });
      return;
    }

    const max =
      values.maxRedemptions === '' || values.maxRedemptions == null
        ? null
        : Number(values.maxRedemptions);

    try {
      const { data } = await dataProvider.create('promoCodes', {
        data: {
          code: values.code.trim().toUpperCase(),
          market,
          effectType: 'fixed_wallet_credit',
          effectValue: Number(values.effectValue),
          validFrom: toIso(values.validFrom),
          validTo: toIso(values.validTo),
          maxRedemptions: max,
          usedCount: 0,
          active: true,
          createdAt: new Date().toISOString(),
        },
      });
      notify('Promo code created', { type: 'success' });
      redirect('list', 'promoCodes', data.id);
    } catch {
      notify('Could not create the promo code', { type: 'error' });
    }
  };

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
        New promo code
      </Typography>
      <Form defaultValues={{ market: lockedMarket }} onSubmit={onSubmit}>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <TextInput
              source="code"
              label="Code"
              validate={required('Code is required')}
              fullWidth
            />
            {locked ? (
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Market
                </Typography>
                <Typography variant="body1">
                  {lockedMarket.toUpperCase()}
                </Typography>
              </Box>
            ) : (
              <SelectInput
                source="market"
                label="Market"
                choices={marketChoices}
                validate={required('Market is required')}
              />
            )}
            {/* Effect is a single-member enum today; the value is what varies. */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Effect type
              </Typography>
              <Typography variant="body1">
                Wallet credit (fixed amount)
              </Typography>
            </Box>
            <NumberInput
              source="effectValue"
              label="Effect value"
              validate={[required('Effect value is required'), minValue(0)]}
            />
            <DateTimeInput
              source="validFrom"
              label="Valid from"
              validate={required('Valid from is required')}
            />
            <DateTimeInput
              source="validTo"
              label="Valid to"
              validate={required('Valid to is required')}
            />
            <NumberInput
              source="maxRedemptions"
              label="Max redemptions (blank = uncapped)"
              validate={minValue(0)}
            />
          </CardContent>
        </Card>
        <Stack direction="row" spacing={2}>
          <Button type="submit" variant="contained">
            Create
          </Button>
          <Button type="button" onClick={() => redirect('list', 'promoCodes')}>
            Cancel
          </Button>
        </Stack>
      </Form>
    </Box>
  );
};

/** Gated entry point registered as the `promoCodes` Resource's create. */
export const PromoCodeCreate = () => (
  <RequireAccess capability="promo_codes:issue">
    <PromoCodeCreateForm />
  </RequireAccess>
);
